import { Loader } from '@getvim/atomic-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { Organisms } from '../../atomic';
import { analyticsClient, VimConnectSeamlessAnalyticsEventTypes } from '../../logic/analytics';
import { close, sendSeamlessApproved } from '../../logic/messaging';
import { SeamlessLoginWidget, SeamlessLoginWidgetProps } from './SeamlessLoginWidget';
import { Login } from '@getvim/vim-connect';

export type SeamlessLoginProps = SeamlessLoginWidgetProps & {
  onSeamlessDenied: () => void;
  signupSessionId: string | undefined;
  signupAppName: string | undefined;
  autoApprove?: boolean;
  showOtherLoginMethodsOption?: boolean;
};

export const SeamlessLogin: React.FC<SeamlessLoginProps> = ({
  onSeamlessDenied,
  autoApprove,
  signupSessionId,
  signupAppName,
  showOtherLoginMethodsOption,
  ...props
}) => {
  const [approved, setIsApproved] = useState(false);
  const seamlessApproved = useCallback(() => {
    setIsApproved(true);
    sendSeamlessApproved();
  }, [setIsApproved]);

  const learnMoreVisibilityChanged = useCallback((visibility) => {
    analyticsClient.track(
      VimConnectSeamlessAnalyticsEventTypes.VimConnectSeamlessConsentLearnMoreVisibility,
      {
        learn_more_visible: visibility,
      },
    );
  }, []);

  const onClose = useCallback(() => {
    close(true, Login.LoginMethodType.SEAMLESS_LOGIN);
  }, []);

  useEffect(() => {
    if (autoApprove) {
      seamlessApproved();
    } else {
      analyticsClient.track(
        VimConnectSeamlessAnalyticsEventTypes.VimConnectSeamlessConsentDisplayed,
        { signup_session_id: signupSessionId! },
      );
    }
  }, [autoApprove, seamlessApproved, signupSessionId]);

  const autoApproveInProgress = autoApprove && !approved;

  if (autoApproveInProgress) {
    return <Loader />;
  }

  return approved ? (
    <SeamlessLoginWidget {...props} />
  ) : (
    <Organisms.ApprovalWidget
      onApproved={seamlessApproved}
      onDenied={onSeamlessDenied}
      onLearnMoreVisibilityChanged={learnMoreVisibilityChanged}
      onClose={onClose}
      showDifferentMethods={
        showOtherLoginMethodsOption || !!(props.ehrCredentials && props.ehrCredentials.password)
      }
      signupAppName={signupAppName}
    />
  );
};
